import React, { useState } from "react";
import SEOHead from "../../section/SeoHead";
import ScrollAnimation from "../../../hooks/ScrollAnimation";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const ServiceDetailLayout = ({
  title,
  subtitle,
  description,
  features = [],
  imageSrc,
  imageAlt,
  customImage,
  highlights = [],
  steps = [],
  faqs = [],
  canonicalUrl,
  structuredData,
}) => {
  const { t } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${title} | Citizen Hub Immigration Services`}
        description={description}
        keywords={`${title.toLowerCase()}, immigration services, immigration forms, us immigration`}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative isolate overflow-hidden text-white py-24 md:py-32">
        {/* dynamic gradient bg */}
        <div className="absolute inset-0 -z-30 animate-[gradientMove_16s_ease_infinite] bg-[linear-gradient(120deg,#1e3a8a_0%,#1d4ed8_50%,#2563eb_100%)] bg-[length:200%_200%] opacity-95" />

        {/* floating balls (glossy blobs) */}
        <div className="pointer-events-none absolute inset-0 -z-20">
          <span className="ball absolute left-[6%] top-[18%]" />
          <span className="ball absolute right-[10%] top-[28%] delay-300" />
          <span className="ball absolute left-[15%] bottom-[18%] delay-700" />
          <span className="ball-sm absolute right-[22%] bottom-[26%] delay-500" />
          <span className="ball-sm absolute left-[28%] top-[36%] delay-1000" />
        </div>

        {/* subtle radial lights */}
        <div
          aria-hidden
          className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-white/10 blur-3xl -z-10"
          style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl -z-10"
          style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
        />

        {/* content */}
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            className="inline-flex items-center gap-2 mb-6 rounded-full border border-white/20 bg-white/10 px-6 py-2 backdrop-blur-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-[pingSlow_2.4s_linear_infinite]" />
            <span className="text-sm font-semibold tracking-wide text-white/90">
              {title}
            </span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-6xl font-black tracking-tight leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,.35)]"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.65 }}
            style={{
              background:
                "linear-gradient(180deg, #ffffff 0%, #e2e8f0 60%, rgba(255,255,255,.85) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {title}
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="mx-auto mt-4 h-[3px] w-48 origin-center rounded-full bg-gradient-to-r from-cyan-300 via-white to-blue-300 shadow-[0_0_18px_rgba(255,255,255,.6)]"
          />

          {subtitle && (
            <motion.p
              className="mt-6 text-lg md:text-[1.25rem] leading-relaxed text-blue-100/90 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        {/* multi-layer animated waves */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none -z-10">
          <svg viewBox="0 0 1440 240" className="w-full h-[110px] md:h-[150px]" preserveAspectRatio="none">
            {/* back wave */}
            <path
              className="animate-[waveDrift_14s_ease_in_out_infinite]"
              fill="rgba(255,255,255,0.20)"
              d="M0,140 C180,160 360,120 540,140 C720,160 900,200 1080,170 C1260,140 1350,120 1440,130 L1440,240 L0,240 Z"
            />
            {/* middle wave */}
            <path
              className="animate-[waveDrift_12s_ease_in_out_infinite_reverse]"
              fill="rgba(255,255,255,0.28)"
              d="M0,150 C200,130 420,170 640,155 C860,140 1000,150 1220,160 C1330,165 1385,165 1440,166 L1440,240 L0,240 Z"
            />
            {/* front highlight wave */}
            <path
              className="animate-[waveDrift_10s_ease_in_out_infinite]"
              fill="rgba(255,255,255,0.36)"
              d="M0,165 C240,170 420,160 620,168 C820,176 1040,154 1240,162 C1340,166 1390,166 1440,166 L1440,240 L0,240 Z"
            />
          </svg>
        </div>

        {/* keyframes + balls styles */}
        <style jsx>{`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes waveDrift {
            0%   { transform: translateX(0px) translateY(0px); }
            50%  { transform: translateX(-20px) translateY(6px); }
            100% { transform: translateX(0px) translateY(0px); }
          }
          @keyframes pingSlow {
            0% { transform: scale(0.85); opacity: 1; }
            70% { transform: scale(1.25); opacity: 0; }
            100% { transform: scale(0.85); opacity: 1; }
          }
          @keyframes floatA {
            0%   { transform: translateY(0) translateX(0) scale(1); }
            50%  { transform: translateY(-16px) translateX(10px) scale(1.03); }
            100% { transform: translateY(0) translateX(0) scale(1); }
          }
          @keyframes floatB {
            0%   { transform: translateY(0) translateX(0) scale(1); }
            50%  { transform: translateY(14px) translateX(-12px) scale(0.98); }
            100% { transform: translateY(0) translateX(0) scale(1); }
          }
          .ball {
            width: 220px; height: 220px; border-radius: 9999px;
            background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.55), rgba(255,255,255,.12) 60%, rgba(255,255,255,0) 70%);
            filter: blur(2px);
            mix-blend-mode: screen;
            animation: floatA 9s ease-in-out infinite;
          }
          .ball-sm {
            width: 130px; height: 130px; border-radius: 9999px;
            background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.45), rgba(255,255,255,.1) 60%, rgba(255,255,255,0) 70%);
            filter: blur(1px);
            mix-blend-mode: screen;
            animation: floatB 11s ease-in-out infinite;
          }
          .delay-300 { animation-delay: .3s !important; }
          .delay-500 { animation-delay: .5s !important; }
          .delay-700 { animation-delay: .7s !important; }
          .delay-1000 { animation-delay: 1s !important; }
        `}</style>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="up" delay={0.1}>
            <motion.div
              className="bg-white border border-gray-200 rounded-lg p-6 md:p-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {description}
                  </p>
                  {features.length > 0 && (
                    <div className="mb-8">
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        {t("service_layout.what_you_get")}
                      </h2>
                      <ul className="space-y-2">
                        {features.map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-start text-gray-700"
                          >
                            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {highlights.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {t("service_layout.why_matters")}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {highlights.map((h, i) => (
                          <div
                            key={i}
                            className="bg-blue-50 border border-blue-100 rounded-md px-4 py-3 text-blue-900"
                          >
                            {h}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="order-first md:order-none">
                  {customImage ? (
                    <div className="relative mx-auto max-w-lg lg:max-w-xl">
                      {customImage}
                    </div>
                  ) : imageSrc ? (
                    <div className="relative mx-auto max-w-lg lg:max-w-xl">
                      <img
                        src={imageSrc}
                        alt={imageAlt || `${title} illustration`}
                        className="relative w-full h-96 lg:h-[28rem] rounded-2xl shadow-lg object-cover"
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              {steps.length > 0 && (
                <div className="mt-16">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-12 text-center">
                    {t("service_layout.how_it_works")}
                  </h2>
                  <div className={`grid grid-cols-1 ${steps.length === 2 ? 'md:grid-cols-2' : steps.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-8 lg:gap-12`}>
                    {steps.map((s, i) => {
                      const getStepIcon = (index, title = "") => {
                        // Choose icon based on step index or title keywords
                        const titleLower = title.toLowerCase();
                        if (titleLower.includes('contact') || titleLower.includes('schedule') || titleLower.includes('book') || index === 0) {
                          // Grid/forms icon for first step
                          return (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                          );
                        } else if (titleLower.includes('review') || titleLower.includes('consultation') || titleLower.includes('connect') || index === 1) {
                          // Link/chain icon for review/consultation
                          return (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          );
                        } else if (titleLower.includes('process') || titleLower.includes('track') || titleLower.includes('monitor') || titleLower.includes('review') || index === 2) {
                          // Gear/settings icon for processing
                          return (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          );
                        } else {
                          // Checkmark/circle icon for completion
                          return (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          );
                        }
                      };

                      return (
                        <div key={i} className="text-center">
                          <div className="flex justify-center mb-6">
                            <div className="bg-gray-900 rounded-lg w-16 h-16 flex items-center justify-center text-white">
                              {getStepIcon(i, s.title)}
                            </div>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-3">
                            {s.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                            {s.content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </ScrollAnimation>

          {faqs.length > 0 && (
            <ScrollAnimation direction="up" delay={0.1}>
              <motion.div
                className="mt-12 bg-white border border-gray-200 rounded-lg p-6 md:p-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t("service_layout.faq_title")}
                </h2>
                <div className="space-y-4">
                  {faqs.map((f, i) => (
                    <motion.div
                      key={i}
                      className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                      whileHover={{ scale: 1.01, boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <button
                        onClick={() => toggleFAQ(i)}
                        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                      >
                        <h3 className="font-semibold text-gray-900 pr-4 flex-1">
                          {f.q}
                        </h3>
                        <motion.div
                          className="shrink-0"
                          animate={{ rotate: openFAQ === i ? 180 : 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <svg
                            className="w-5 h-5 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </motion.div>
                      </button>
                      <AnimatePresence>
                        {openFAQ === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-5">
                              <div className="border-t border-gray-200 pt-4">
                                <motion.p
                                  className="text-gray-700 leading-relaxed"
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.1, duration: 0.3 }}
                                >
                                  {f.a || f.content}
                                </motion.p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </ScrollAnimation>
          )}
        </div>
      </section>

      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollAnimation direction="up" delay={0.1}>
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {t("service_layout.need_help")}
            </motion.h2>
            <motion.p
              className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {t("service_layout.help_desc")}
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <a
                href="tel:+1 3108802133"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                {t("service_layout.call_btn")}
              </a>
              <a
                href="mailto:info@citznhub.com"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                {t("service_layout.email_btn")}
              </a>
            </motion.div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailLayout;
